/* eslint-disable react-hooks/exhaustive-deps */
import { WorkspaceSubscriptionPlan } from '@airimpact/types/modelEnums'
import constate from 'constate'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { useProjectsContext } from './useProjects'
import { useUsersContext } from './useUsers'
import { useWorkspaceContext } from './useWorkspace'
import { useZippieIdContext } from './useZippieId'
import apiClient from '../api/apiClient'

interface Interval {
  _id: string
  interval: string
  interval_count: number
  currency: string
  unit_amount_decimal: string
  unit_amount_formatted: string
}

export interface PlanDescription {
  text: string
  feature_limit: string
  hint: string | null
  coming_soon: boolean
}

export interface PlanDetails {
  plan: string
  limits: {
    projects: number
  }
  description: PlanDescription[]
}

export interface PlanData {
  _id: string
  plan: string
  key: string
  name: string
  description: string
  product_type: string
  intervals: Interval[]
}

export interface Addon {
  _id: string
  plan: string
  key: string
  name: string
  description: string
  product_type: string
  intervals: Interval[]
}

interface GetSubscriptionPlansResponse {
  status: string
  code: number
  description: PlanDetails[]
  data: {
    plans: PlanData[]
    addons: Addon[]
  }
}

export interface Price {
  period: {
    start: number
    end: number
  }
  interval: 'month'
  currency: 'usd'
  amount: string
  amount_formatted: string
}

export interface Invoice {
  amount_due: number
  amount_formatted: number
  amount_paid: number
  amount_paid_formatted: number
  created_at: string
  effective_at: number
}

export interface SubscriptionProductItem {
  product_id: string
  quantity: number
}

export interface SubscriptionData {
  _id: string
  _companyId: string
  _customerId: string
  provider: string
  data: {
    transaction_type: string
    interval: string
    items: SubscriptionProductItem[]
    transaction_status: string
    transaction_id: string
    expires_at: Date | null
    cancelled_at: Date | null
  }
  created_at: Date | null
  updated_at: Date | null
  __v: number
}

interface SubscriptionPhase {
  active: boolean
  end_date: number
  items: SubscriptionProductItem[]
  start_date: number
  trial_end: null | number
  interval: string
}

export interface SubscriptionProvider {
  client_secret: string | null
  stripe_key: string
  invoice: {
    amount_due: number
    amount_formatted: string
    amount_paid: number
    amount_paid_formatted: string
    created_at: number
    effective_at: Date | null
  }
  period: {
    start: number
    end: number
  }
  plan: {
    interval: string
    currency: string
    amount: string
    amount_formatted: string
  }
  trial: {
    start: number
    end: number
  }
  phases: null | SubscriptionPhase[]
}

interface Subscription {
  plan: string
  status: 'trialing'
  price: Price
  invoice: Invoice
  trial: {
    start: number
    end: number
  }
  data: SubscriptionData
  provider: SubscriptionProvider
}

interface CustomerProfileData {
  _id: string
  _companyId: string
  provider: string
  data: {
    name: string
    email: string
    customer_id: string
  }
  created_at: Date | null
  updated_at: Date | null
  __v: number
}

interface CustomerProfileProviderAddress {
  city: string
  country: string
  line1: string
  line2: string | null
  postal_code: string
  state: string
}

export interface CustomerProfilePaymentMethod {
  id: string
  default: boolean | null
  billing_details: {
    address: CustomerProfileProviderAddress
    email: string | null
    name: string
    phone: string | null
    firstName?: string | undefined
    lastName?: string | undefined
  }
  card: {
    brand: string
    country: string
    display_brand: string
    exp_month: number
    exp_year: number
    funding: string
    last4: string
  }
}

interface TaxId {
  id: string
  country: string
  type: string
  value: string
  verification: {
    status: string
    verified_address: null | string
    verified_name: null | string
  }
}

interface CustomerProfileProvider {
  profile: {
    id: string
    name: string
    email: string
    phone: string | null
    address: CustomerProfileProviderAddress
  }
  payment_methods: CustomerProfilePaymentMethod[]
  tax_ids: TaxId[]
}

export interface ICustomerProfileResponse {
  data: CustomerProfileData
  provider: CustomerProfileProvider
}

interface IBillingHistory {
  id: string
  name: string
  amount_due: number
  amount_paid: number
  amount_due_formatted: string
  amount_paid_formatted: string
  created_at: number
  status: number
  invoice_pdf: string
}

export interface ITaxType {
  type: string
  country: string
  country_label: string
  format: string
  placeholder: string
  stripe_type: string
  verifiable: boolean
  description: string
}

export interface ISubscribePayload {
  interval?: string
  items: SubscriptionProductItem[]
}

export interface ISubscribeUpdatePayload {
  immediate: boolean
  items: SubscriptionProductItem[]
}

export interface ITaxDetailsPayload {
  type: string
  value: string
}

export const useTierSelection = () => {
  const [subscriptionPlansDetails, setSubscriptionPlansDetails] = useState<PlanDetails[]>([])
  const [subscriptionPlans, setSubscriptionPlans] = useState<PlanData[]>([])
  const [subscriptionAddons, setSubscriptionAddons] = useState<Addon[]>([])
  const [customerProfile, setCustomerProfile] = useState<ICustomerProfileResponse>()
  const [billingHistory, setBillingHistory] = useState<IBillingHistory[]>([])
  const [currentPlanName, setCurrentPlanName] = useState<string>('')
  const [currentPlanDetails, setCurrentPlanDetails] = useState<Subscription>()
  const [stripeKey, setStripeKey] = useState<string>('')
  const [isCurrentPlanDetailsLoading, setIsCurrentPlanDetailsLoading] = useState(true)

  const {
    updateSelectedWorkspace,
    selectedWorkspaceDetails,
    isWorkspaceFetchingInProgress,
    selectedNodeId,
    currentPlanDetailsIsLoadedRef,
    customerProfileIsLoadedRef,
    subscriptionPlansIsLoadedRef,
    billingHistoryIsLoadedRef,
  } = useWorkspaceContext()
  const { dashboardUsers } = useUsersContext()
  const { fetchProjects } = useProjectsContext()
  const { isLoggedIn } = useZippieIdContext()

  const { workspaceId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const defaultWorkspaceId = workspaceId || selectedNodeId
  const isFreePlan = currentPlanName === WorkspaceSubscriptionPlan.Free
  const isEssentialPlan = currentPlanName === WorkspaceSubscriptionPlan.Essentials

  const getCustomerProfile = async () => {
    try {
      const response = await apiClient.get(`/workspace/${defaultWorkspaceId}/subscriptions/customer/profile`)
      setCustomerProfile(response.data.data)
      customerProfileIsLoadedRef.current = true
      return response.data.data
    } catch (e) {
      console.error(e)
    }
  }
  const updateCustomerProfile = async (data: {
    name?: string
    phone?: string | null
    address?: CustomerProfileProviderAddress
    default_payment_method?: string
  }) => {
    const response = await apiClient.patch(`/workspace/${defaultWorkspaceId}/subscriptions/customer/profile`, {
      ...data,
    })
    await getCustomerProfile()
    return response.data.data
  }

  const getCurrentSubscription = async () => {
    try {
      const response = await apiClient.get(`/workspace/${defaultWorkspaceId}/subscriptions/current`)
      setCurrentPlanDetails(response.data.data)
      if (response.data.data.provider?.stripe_key) setStripeKey(response.data.data.provider.stripe_key)
      currentPlanDetailsIsLoadedRef.current = true
      return response.data.data
    } catch (e) {
      console.error(e)
    }
  }

  const getSubscriptionHistory = async () => {
    const response = await apiClient.get(`/workspace/${defaultWorkspaceId}/subscriptions/history`)
    setBillingHistory(response.data.data)
    billingHistoryIsLoadedRef.current = true
    return response.data.data
  }

  const addNewPaymentMethod = async () => {
    const response = await apiClient.post(`/workspace/${workspaceId}/subscriptions/customer/payment_method`)
    return response.data.data
  }

  const removePaymentMethod = async ({ paymentMethodId }: { paymentMethodId: string }) => {
    const response = await apiClient.delete(
      `/workspace/${workspaceId}/subscriptions/customer/payment_method/${paymentMethodId}`,
      { data: { paymentMethodId } },
    )
    await getCustomerProfile()
    return response.data.data
  }

  const subscribe = async (payload: ISubscribePayload) => {
    const response = await apiClient.post(`/workspace/${workspaceId}/subscriptions/subscribe`, payload)
    return response.data.data
  }

  const editSubscription = async (payload: ISubscribeUpdatePayload) => {
    const response = await apiClient.patch(`/workspace/${workspaceId}/subscriptions/update`, payload)
    currentPlanDetailsIsLoadedRef.current = false
    customerProfileIsLoadedRef.current = false
    subscriptionPlansIsLoadedRef.current = false
    billingHistoryIsLoadedRef.current = false
    return response.data.data
  }
  const unsubscribe = async () => {
    await apiClient.post(`/workspace/${workspaceId}/subscriptions/unsubscribe`, {
      immediate: false,
    })
    // eslint-disable-next-line no-promise-executor-return
    await new Promise((resolve) => setTimeout(resolve, 1000))

    await updateSelectedWorkspace()
    await fetchProjects()
  }

  const getSubscriptionPlans = async (workspaceId?: string) => {
    try {
      const endpoint = workspaceId ? `/workspace/${workspaceId}/subscriptions/plans` : `/subscriptions/plans`

      const { data } = await apiClient.get<GetSubscriptionPlansResponse>(endpoint)

      setSubscriptionPlansDetails(data.description)
      setSubscriptionPlans(data.data.plans)
      setSubscriptionAddons(data.data.addons)
      subscriptionPlansIsLoadedRef.current = true
    } catch (error) {
      console.error('Error fetching subscription plans:', error)
    }
  }

  const getSubscriptionPricesPreview = async (payload: any) => {
    const { data } = await apiClient.post(`/workspace/${workspaceId}/subscriptions/update/preview`, payload)
    return data.data
  }

  const fetchTaxTypes = async () => {
    const response = await apiClient.get(`/workspace/${workspaceId}/subscriptions/customer/tax_type`)
    return response.data.data
  }

  const addTaxDetails = async (payload: ITaxDetailsPayload) => {
    await apiClient.post(`/workspace/${workspaceId}/subscriptions/customer/tax`, payload)
  }

  const updateTaxDetails = async (taxId: string, payload: ITaxDetailsPayload) => {
    await apiClient.patch(`/workspace/${workspaceId}/subscriptions/customer/tax/${taxId}`, payload)
  }

  const deleteTaxDetails = async (taxId: string) => {
    await apiClient.delete(`/workspace/${workspaceId}/subscriptions/customer/tax/${taxId}`)
  }

  useEffect(() => {
    if (!isLoggedIn) return

    if (
      !subscriptionPlansIsLoadedRef.current &&
      (location.pathname === `/admin` || location.pathname === `/admin/invites`)
    ) {
      getSubscriptionPlans()
    }

    if (dashboardUsers.length === 0 || isWorkspaceFetchingInProgress) return

    if (!selectedWorkspaceDetails.subscription?.data) {
      navigate(`${workspaceId}/tier-selection`)
    }

    setIsCurrentPlanDetailsLoading(false)
  }, [isLoggedIn, selectedWorkspaceDetails, dashboardUsers, isWorkspaceFetchingInProgress, location.pathname])

  useEffect(() => {
    if (isWorkspaceFetchingInProgress || isCurrentPlanDetailsLoading) {
      return
    }
    if (selectedWorkspaceDetails.subscription?.data && subscriptionPlans.length > 0) {
      const selectedPlan = subscriptionPlans?.find((plan) =>
        selectedWorkspaceDetails.subscription?.data?.data.items.some((item: any) => item.product_id === plan._id),
      )
      setCurrentPlanName(selectedPlan?.plan || '')
    }
  }, [isCurrentPlanDetailsLoading, selectedWorkspaceDetails, subscriptionPlans, isWorkspaceFetchingInProgress])

  return {
    subscriptionPlansDetails,
    subscriptionPlans,
    subscriptionAddons,
    currentPlanDetails,
    currentPlanName,
    isFreePlan,
    isEssentialPlan,
    setCurrentPlanName,
    customerProfile,
    billingHistory,
    stripeKey,
    subscribe,
    unsubscribe,
    editSubscription,
    addNewPaymentMethod,
    removePaymentMethod,
    updateCustomerProfile,
    getCustomerProfile,
    getSubscriptionPricesPreview,
    addTaxDetails,
    updateTaxDetails,
    deleteTaxDetails,
    fetchTaxTypes,
    getSubscriptionHistory,
    getCurrentSubscription,
    getSubscriptionPlans,
    isCurrentPlanDetailsLoading,
    currentPlanDetailsIsLoadedRef,
    customerProfileIsLoadedRef,
    subscriptionPlansIsLoadedRef,
    billingHistoryIsLoadedRef,
  }
}

const [TierSelectionContainer, useTierSelectionContext] = constate(useTierSelection)

export { TierSelectionContainer, useTierSelectionContext }
